import { yupResolver } from "@hookform/resolvers/yup";
import { type FC } from "react";
import { type DeepPartial } from "react-hook-form";
import styled from "styled-components";

import { useRootSelector } from "@ds/hooks";

import { isAggregatedEntity } from "@ds/model/entity-model";

import { getPlayerStatusIndicator } from "@ds/modules/devices/players/components/common/status-indicator";
import { selectTemplateExperienceRoles } from "@ds/modules/settings/projects/redux/selectors/experience-formation-selectors";
import { hasFormationRoles } from "@ds/modules/settings/projects/utils/formation-roles";
import { selectCurrentUserProjectId } from "@ds/modules/settings/users/redux/selectors";

import {
  DetailsCard,
  DetailsCardRow,
  DetailsCardRowWrapper,
  EditableDetailsCardDecorator,
  EditableDetailsCardRow,
  EditableDetailsCardRowDecorator,
} from "@ds/components/details-view";
import { MultipleValuesLabelWithLink, UnassignedLabel } from "@ds/components/forms/labels/common";
import { DetailsTable } from "@ds/components/table/details-table";
import { Column, getReorderColumn } from "@ds/components/table/table-columns";
import { TextLink } from "@ds/components/text-link";
import { isMultipleFieldValuesInAggregatedEntity } from "@ds/utils/entities";
import { composeDetailsRouteByEntityType } from "@ds/utils/router";

import { updateValidationSchema } from "../../utils/validation-schema";
import { ExperiencePlayersControl } from "../forms/controls/experience-players-control";
import { ExperienceDetailsPlayerRolesCard } from "./experience-details-player-roles-card";
import { ExperienceDetailsPlayersCardActionsMenu } from "./experience-details-players-card-actions-menu";
import { useExperiencePlayersReorder } from "./use-experience-players-reorder";

export const Wrapper = styled(DetailsCardRowWrapper)`
  :last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const ExperienceDetailsPlayersListCard: FC<{
  experience: Experience;
  onSubmit: (changedFields: DeepPartial<Experience>) => void;
}> = ({ experience, onSubmit }) => {
  const {
    aggregatedExperiencePlayers,
    tablePlayers,
    isReordering,
    onSaveReorderHandler,
    onCancelReorderHandler,
    onTableRowReorderHandler,
  } = useExperiencePlayersReorder(experience);

  const onSubmitHandler = ({ players }: DeepPartial<ExperienceFormData>) =>
    onSubmit({ device_ids: players?.map(player => player?.id).filter(_ => _) });

  return (
    <EditableDetailsCardDecorator<ExperienceFormData>
      initialValue={{ experience, players: aggregatedExperiencePlayers }}
      validationResolver={yupResolver(updateValidationSchema.players)}
      onSubmit={onSubmitHandler}
    >
      {cardProps => (
        <DetailsCard
          title="Players"
          icon="desktop"
          actions={
            <ExperienceDetailsPlayersCardActionsMenu
              mode={isReordering ? "REORDERING" : cardProps.isEditing ? "EDITING" : "DEFAULT"}
              isDirty={cardProps.isDirty}
              isAggregated={isAggregatedEntity(cardProps.entity.experience)}
              onEdit={cardProps.onEditRow("players")}
              onReset={cardProps.onCancelRow("players")}
              onSubmit={cardProps.handleSubmit(cardProps.onSubmitForm)}
              onSaveReorder={onSaveReorderHandler}
              onCancelReorder={onCancelReorderHandler}
            />
          }
        >
          {/* Display mode: Show DetailsTable */}
          {!cardProps.isEditing && !isAggregatedEntity(cardProps.entity.experience) && (
            <>
              {cardProps.entity.players?.length ? (
                <DetailsTable
                  value={tablePlayers}
                  className="experience-players-table"
                  onRowReorder={onTableRowReorderHandler}
                >
                  {getReorderColumn({ rowReorder: true })}
                  <Column
                    header="Player Name"
                    body={(player: Player, props) => (
                      <>
                        <span className="text-sm text-gray-500">{props.rowIndex + 1}.</span>
                        {` `}
                        <TextLink to={composeDetailsRouteByEntityType(player)}>{player.name}</TextLink>
                      </>
                    )}
                    resizeable={false}
                  />
                  <Column
                    header="Status"
                    body={(player: Player) => getPlayerStatusIndicator(player)}
                    className="w150"
                    resizeable={false}
                  />
                </DetailsTable>
              ) : (
                <DetailsCardRow label="Choose players">
                  <UnassignedLabel text="Experience(s) has no players" />
                </DetailsCardRow>
              )}
            </>
          )}

          {/* Aggregated entity: Show multiple values label */}
          {!cardProps.isEditing && isAggregatedEntity(cardProps.entity.experience) && (
            <DetailsCardRow label="Choose players">
              {isMultipleFieldValuesInAggregatedEntity(cardProps.entity.experience, "device_ids") ? (
                <MultipleValuesLabelWithLink to={composeDetailsRouteByEntityType(aggregatedExperiencePlayers)} />
              ) : (
                <UnassignedLabel text="Experience(s) has no players" />
              )}
            </DetailsCardRow>
          )}

          {/* Edit mode: Show current editing interface */}
          {cardProps.isEditing && (
            <EditableDetailsCardRowDecorator
              field="players"
              input={<ExperiencePlayersControl control={cardProps.control} autoFocus excludeLabel />}
              shouldEditWholeCard
              shouldHideEdit={isAggregatedEntity(cardProps.entity.experience)}
              {...cardProps}
            >
              {rowProps => <EditableDetailsCardRow label="Choose players" editableSettings={rowProps} />}
            </EditableDetailsCardRowDecorator>
          )}
        </DetailsCard>
      )}
    </EditableDetailsCardDecorator>
  );
};

export const ExperienceDetailsPlayersCard: FC<{
  experience: Experience;
  onSubmit: (changedFields: DeepPartial<Experience>) => void;
}> = ({ experience, onSubmit }) => {
  const projectId = useRootSelector(selectCurrentUserProjectId);
  const roles = useRootSelector(state => selectTemplateExperienceRoles(state, projectId));

  // players get roles one experience at a time; several selected experiences keep the plain players list
  if (hasFormationRoles(roles) && !isAggregatedEntity(experience)) {
    return <ExperienceDetailsPlayerRolesCard experience={experience} roles={roles} onSubmit={onSubmit} />;
  }

  return <ExperienceDetailsPlayersListCard experience={experience} onSubmit={onSubmit} />;
};
